/**
 * Utility functions to format dates and times
 * 
 * IMPORTANT NOTE: 
 * - Database (Aurora RDS) stores timestamps in UTC
 * - Backend sends timestamps in ISO 8601 format with 'Z' suffix (e.g., "2026-02-03T18:44:06Z")
 * - JavaScript automatically converts UTC to browser's local timezone when the 'Z' is present
 * - Display shows the time in user's local timezone with timezone abbreviation
 * 
 * Example:
 * - Database (UTC): 2026-02-03 16:34:43
 * - Backend sends: "2026-02-03T16:34:43Z"
 * - Browser (EST): Parses as UTC, displays as "02/03/2026, 11:34:43 AM EST"
 */

/**
 * Format a date/timestamp to a readable string in the user's local timezone
 * @param {string|Date} dateString - Date string with 'Z' for UTC or Date object
 * @param {boolean} includeSeconds - Whether to include seconds in the output
 * @returns {string} Formatted date string
 */
export const formatDateTime = (dateString, includeSeconds = true) => {
    if (!dateString) return "—";

    try {
        // Parse the date - JavaScript will correctly interpret 'Z' as UTC and convert to local
        const date = new Date(dateString);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return dateString;
        }

        const options = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            ...(includeSeconds && { second: '2-digit' }),
            hour12: true,
            timeZone: getUserTimezone()
        };

        return date.toLocaleString(undefined, options);
    } catch {
        return dateString;
    }
};

/**
 * Format a date/timestamp with timezone abbreviation
 * @param {string|Date} dateString - Date string with 'Z' for UTC or Date object
 * @returns {string} Formatted date string with timezone abbreviation (e.g., "02/03/2026, 11:34:43 AM EST")
 */
export const formatDateTimeWithZone = (dateString) => {
    if (!dateString) return "—";

    try {
        // Parse the date - JavaScript will correctly interpret 'Z' as UTC and convert to local
        const date = new Date(dateString);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return dateString;
        }

        const options = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZoneName: 'short', // Adds timezone abbreviation like "EST", "PST", etc.
            timeZone: getUserTimezone()
        };

        return date.toLocaleString(undefined, options);
    } catch {
        return dateString;
    }
};

/**
 * Get the user's timezone name
 * @returns {string} Timezone name (e.g., "America/New_York")
 */
export const getUserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'Unknown';
    }
};

/**
 * Format date only (no time)
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date string (e.g., "02/03/2026")
 */
export const formatDateOnly = (dateString) => {
    if (!dateString) return "—";

    try {
        const date = new Date(dateString);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return dateString;
        }

        const options = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: getUserTimezone()
        };

        return date.toLocaleDateString(undefined, options);
    } catch {
        return dateString;
    }
};